import React, { useMemo, useState } from "react";

import {
  Input,
  message,
  Modal,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import { toggleMangoSignupService } from "../Pages/api";

/**
 * A mango is the creator's signup service ("free mango") when it is free and
 * not a public mango — this mirrors how the backend resolves it at signup time.
 */
const isSignupService = (mango) => Boolean(mango?.isFree) && !mango?.isPublic;

/**
 * Mango list for a creator, with a frontend-only name search and the
 * signup-service toggle. Only one mango per creator may be the signup service;
 * the backend rejects a second one and the error is surfaced here.
 */
const CreatorMangoesTable = ({
  mangoes,
  onSignupServiceChange,
  canManageSignupService,
}) => {
  const [search, setSearch] = useState("");
  const [togglingMangoId, setTogglingMangoId] = useState(null);

  const filteredMangoes = useMemo(() => {
    const list = mangoes || [];
    const term = search.trim().toLowerCase();

    if (!term) return list;
    return list.filter((mango) =>
      (mango?.title || "").toLowerCase().includes(term)
    );
  }, [mangoes, search]);

  const handleToggle = async (mango, enabled) => {
    setTogglingMangoId(mango._id);
    try {
      const resp = await toggleMangoSignupService(mango._id, enabled);
      if (resp.code === 0) {
        message.success(
          enabled
            ? "Signup service enabled"
            : "Signup service disabled. Price is now 0 — set it again if this mango should be paid."
        );
        onSignupServiceChange?.();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    } finally {
      setTogglingMangoId(null);
    }
  };

  const confirmToggle = (mango, enabled) => {
    Modal.confirm({
      title: enabled
        ? "Enable this mango as the signup service?"
        : "Disable the signup service?",
      content: enabled
        ? `"${mango.title}" will be made free and hidden, and its price set to 0. Every new user signing up will be subscribed to it.`
        : `"${mango.title}" will no longer be free or hidden. Its price stays 0, so set it again if this mango should be paid.`,
      okText: enabled ? "Enable" : "Disable",
      okType: enabled ? "primary" : "danger",
      cancelText: "Cancel",
      onOk: () => handleToggle(mango, enabled),
    });
  };

  const columns = [
    {
      title: "Mango Name",
      dataIndex: "title",
      key: "title",
      render: (text, record) =>
        isSignupService(record) ? (
          <Typography.Text strong>
            {text} <span title="Signup service">*</span>
          </Typography.Text>
        ) : (
          text
        ),
    },
    {
      title: "Recurring Type",
      dataIndex: "recurringType",
      key: "recurringType",
    },
    {
      title: "Active Subscribers",
      dataIndex: "activeSubscribers",
      key: "activeSubscribers",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (text, record) => `${record?.currency} ${text}`,
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("DD MMM YYYY"),
    },
    {
      title: "Signup Service",
      key: "signupService",
      render: (_, record) => {
        const publicMango = Boolean(record?.isPublic);
        let tooltip = "";
        if (!canManageSignupService) {
          tooltip = "You do not have permission to manage the signup service";
        } else if (publicMango) {
          tooltip = "Public mangoes cannot be used as a signup service";
        }

        return (
          <Tooltip title={tooltip}>
            <Switch
              checked={isSignupService(record)}
              loading={togglingMangoId === record._id}
              disabled={
                !canManageSignupService ||
                Boolean(togglingMangoId) ||
                publicMango
              }
              onChange={(checked) => confirmToggle(record, checked)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Input.Search
        placeholder="Search mango by name"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320 }}
      />
      <Table
        dataSource={filteredMangoes}
        columns={columns}
        rowKey={(record) => record._id}
        pagination={{
          pageSize: 5,
        }}
        style={{
          width: "100%",
        }}
      />
    </Space>
  );
};

CreatorMangoesTable.propTypes = {
  mangoes: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      title: PropTypes.string,
      recurringType: PropTypes.string,
      activeSubscribers: PropTypes.number,
      price: PropTypes.number,
      currency: PropTypes.string,
      createdAt: PropTypes.string,
      isFree: PropTypes.bool,
      isPublic: PropTypes.bool,
    })
  ),
  onSignupServiceChange: PropTypes.func,
  canManageSignupService: PropTypes.bool,
};

CreatorMangoesTable.defaultProps = {
  mangoes: [],
  onSignupServiceChange: undefined,
  // Fail closed: the toggle stays locked unless the permission is passed in.
  canManageSignupService: false,
};

export default CreatorMangoesTable;
